/**
 * FaxTab — v2's Fax surface, natively implemented.
 *
 * Replaces the previous iframe embeds. Uses only existing endpoints
 * (see pages/comms/fax/faxAPI.js). Retires the legacy Fax.jsx and
 * HealthcareFax.jsx once the /fax and /healthcare/fax routes redirect
 * here.
 *
 * PART 7 layout:
 *   ┌──────────────────────────────┬──────────────┐
 *   │ Sub-tabs (Received / Sent /   │              │
 *   │  Retry queue / Templates)     │  Send panel  │
 *   ├──────────────────────────────┤  (persistent │
 *   │                                │   right col) │
 *   │  Sub-tab content               │              │
 *   │                                │              │
 *   └──────────────────────────────┴──────────────┘
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Inbox, Send, RotateCcw, PenTool } from "lucide-react";
import { faxAPI } from "./fax/faxAPI";
import ReceivedGrid from "./fax/ReceivedGrid";
import SendPanel from "./fax/SendPanel";
import SentList from "./fax/SentList";
import RetryQueue from "./fax/RetryQueue";
import TemplatesEditor from "./fax/TemplatesEditor";
import { NAVY, NAVY_HAIRLINE } from "./tokens";

const SUB_TABS = [
  { k: "received",  label: "Received",    icon: Inbox },
  { k: "sent",      label: "Sent",        icon: Send },
  { k: "retry",     label: "Retry queue", icon: RotateCcw },
  { k: "templates", label: "Templates",   icon: PenTool },
];

export default function FaxTab() {
  const [sub, setSub] = useState("received");
  const navigate = useNavigate();

  // Load received faxes when the sub-tab is active. Poll every 20s so
  // freshly-arrived faxes surface without a manual refresh.
  const [received, setReceived] = useState([]);
  const [loadingReceived, setLoadingReceived] = useState(false);
  const [receivedGap, setReceivedGap] = useState("");
  const loadReceived = useCallback(async () => {
    setLoadingReceived(true); setReceivedGap("");
    try {
      const r = await faxAPI.listIncoming(40);
      setReceived(Array.isArray(r) ? r : (r?.items || []));
    } catch (e) {
      setReceivedGap(e.status === 401 || (e.message || "").toLowerCase().includes("humblefax") || (e.message || "").includes("401")
        ? "Fax provider auth failed — check HumbleFax credentials in /admin."
        : `Endpoint /api/fax/incoming errored (${e.message}).`);
      setReceived([]);
    } finally { setLoadingReceived(false); }
  }, []);
  useEffect(() => {
    if (sub !== "received") return;
    loadReceived();
    const t = setInterval(loadReceived, 20000);
    return () => clearInterval(t);
  }, [sub, loadReceived]);

  return (
    <div className="flex-1 flex overflow-hidden bg-slate-50">
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Sub-tabs */}
        <div className="shrink-0 flex items-center gap-1 px-4 pt-3 border-b bg-white" style={{ borderColor: NAVY_HAIRLINE }}>
          {SUB_TABS.map(t => {
            const Icon = t.icon;
            const active = sub === t.k;
            return (
              <button
                key={t.k}
                onClick={() => setSub(t.k)}
                className={`h-10 px-3 rounded-t-md text-[13px] font-semibold flex items-center gap-1.5 border-b-2 -mb-[1px] ${active ? "" : "text-slate-500 hover:bg-slate-50"}`}
                style={active ? { borderColor: NAVY, color: NAVY, background: "white" } : { borderColor: "transparent" }}
                data-testid={`comms2-fax-tab-${t.k}`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto">
          {sub === "received"  && <ReceivedGrid navigate={navigate} faxes={received} loading={loadingReceived} gap={receivedGap} onRefresh={loadReceived} />}
          {sub === "sent"      && <SentList />}
          {sub === "retry"     && <RetryQueue />}
          {sub === "templates" && <TemplatesEditor />}
        </div>
      </div>
      <SendPanel />
    </div>
  );
}
